import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  PLATFORM_ID,
  Renderer2,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import gsap from 'gsap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'rp-home-marquee',
  standalone: true,
  templateUrl: './marquee.component.html',
  styleUrl: './marquee.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
})
export class MarqueeComponent implements AfterViewInit {
  readonly marquees = [
    'general.offers',
    'general.advertisers',
    'general.opportunities',
    'general.support',
    'general.experience',
    'general.payments',
  ];

  readonly firstItem = signal<HTMLDivElement>(null);

  private readonly track: Signal<ElementRef<HTMLDivElement>> = viewChild('track', {
    read: ElementRef<HTMLDivElement>,
  });

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _renderer = inject(Renderer2);
  private readonly _translateService = inject(TranslateService);

  ngAfterViewInit(): void {
    this._createMarqueeAnimation();

    this._translateService.onLangChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const translatedOffers = this._translateService.instant(`general.offers`);

      if (this.firstItem()) {
        this.firstItem().textContent = translatedOffers;
      }
    });
  }

  private _createMarqueeAnimation(): void {
    const track: HTMLDivElement = this.track().nativeElement;
    const items: HTMLCollection = track.children;

    if (!items.length || !isPlatformBrowser(this._platformId)) return;

    const tl = gsap.timeline({
      repeat: -1,
      defaults: {
        ease: 'expo.inOut',
        duration: 1,
        delay: 1,
      },
    });

    Array.from(items).forEach((_, index) => {
      const distance = (index + 1) * -100;

      tl.to(track, { yPercent: distance });
    });

    this.firstItem.set(items[0]?.cloneNode(true) as HTMLDivElement);

    if (this.firstItem()) {
      this._renderer.appendChild(track, this.firstItem());
    }
  }
}
